import React from 'react';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns-tz';
import ModelSeriesBlockedSelector from '@/components/ModelSeriesBlockedSelector/ModelSeriesBlockedSelector';
import InputText from '@/components/UI/InputText/InputText';
import InputUser from '@/components/UI/InputUser/InputUser';
import { DAY_DATE_FORMAT } from '@/helpers/constants';
import { getAvailableComment } from '@/helpers/utils';
import { PlantDialogWorkflow } from '@/types';
import styles from './DialogPlantWorkflow.module.scss';

type Comment = {
  commentValue: string;
  commentLabel: string;
};

type StatusDialogPlantSectionProps = {
  plantWorkflow: PlantDialogWorkflow;
  title: string;
  statusKey: string;
  headerLanguage: string;
  showActionTakenBy?: boolean;
  showVersion?: boolean;
  labelComments: (comment: Comment) => string;
};

const StatusDialogPlantSection: React.FC<StatusDialogPlantSectionProps> = (props) => {
  const { plantWorkflow, title, statusKey, headerLanguage, showActionTakenBy = false, showVersion = true, labelComments } = props;
  const { t } = useTranslation();

  const getDate = (date: string) => date && format(new Date(date), DAY_DATE_FORMAT);
  const isApproval = ['inApprovalE3'].includes(statusKey);

  return (
    <div className={styles.Section}>
      <h3 className={styles.DialogPlantSectionTitle}>{title}</h3>
      {plantWorkflow?.[statusKey]?.assignedUserCode
        && (
          <div className={styles.OneColumns}>
            <InputUser
              id={`input-${statusKey}-assigned-user`}
              label={t('dialog.statusAsignedUser')}
              title={t('dialog.statusAsignedUser')}
              value={plantWorkflow?.[statusKey]?.assignedUser}
              code={plantWorkflow?.[statusKey]?.assignedUserCode}
              disabled
            />
          </div>
        )}
      <div className={styles.Flex}>
        {showActionTakenBy && (
          <div className={styles.TwoColumns}>
            <InputUser
              id={`input-${statusKey}-action-taken-by`}
              label={t('dialog.statusActionTakenBy')}
              title={t('dialog.statusActionTakenBy')}
              value={plantWorkflow?.[statusKey]?.actionTakenBy}
              code={plantWorkflow?.[statusKey]?.actionTakenByUserCode}
              disabled
            />
          </div>
        )}
        {showVersion && (
          <div className={styles.TwoColumns}>
            <InputText
              id={`input-${statusKey}-version`}
              label={t('dialog.statusVersion')}
              value={plantWorkflow?.[statusKey]?.version ?? 1}
              disabled
            />
          </div>
        )}
        {plantWorkflow?.[statusKey]?.timestamp && (
          <div className={styles.TwoColumns}>
            <InputText
              id={`input-${statusKey}-timestamp`}
              label={t('dialog.statusTimestamp')}
              value={getDate(plantWorkflow?.[statusKey]?.timestamp)}
              disabled
            />
          </div>
        )}
      </div>
      {plantWorkflow?.[statusKey]?.comments?.map((comment: Comment, index: number) => (
        <div className={styles.OneColumns} key={`${index}-comment`}>
          <InputText
            id={`input-${statusKey}-comments-campaign-manager-${index}`}
            label={labelComments(comment)}
            multiline
            rows={4}
            value={getAvailableComment(comment.commentValue, headerLanguage)}
            disabled
          />
        </div>
      ))}
      {(isApproval && plantWorkflow?.inApprovalE3?.blockVehicles !== null)
        && (
          <div className={styles.OneColumns}>
            <p className={styles.MSBlockedTitle}>{t('buttonsHeader.msBlockedSectionTitle')}</p>
            <div className={styles.TwoColumns}>
              <ModelSeriesBlockedSelector
                disabled
                blockVehicles={plantWorkflow?.inApprovalE3?.blockVehicles}
              />
            </div>
          </div>
        )}
    </div>
  );
};

export default StatusDialogPlantSection;
